import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import { Carousel, Button } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { MeetingContext } from "@/pages/Meeting";
import VideoCard from "../VideoCard";
import style from "./style.module.scss";
//import { useUpdateEffect } from "ahooks"
interface GalleryLayoutProps {
  pageSize: number;
}

const GalleryLayout = ({ pageSize }: GalleryLayoutProps) => {
  const { displayParticipants: participants, service, isMonitorMode } = useContext(MeetingContext);
  const carousel = useRef<any>();
  const [page, setPage] = useState(0);

  // 过滤掉共享流（share=true 的虚拟 peer 不参与宫格）
  const nonShareParticipants = useMemo(
    () => participants.filter((p: any) => p.share !== true),
    [participants]
  );

  // 仅用于触发 effect 的稳定依赖：成员身份/顺序变化也会触发
  const nonShareIds = useMemo(
    () => nonShareParticipants.map(p => p.peerId).join(","),
    [nonShareParticipants]
  );

  // 分页切片
  const pagination = useMemo(() => {
    const pages: any[][] = [];
    for (let i = 0; i < nonShareParticipants.length; i += pageSize) {
      pages.push(nonShareParticipants.slice(i, i + pageSize));
    }
    return pages;
  }, [nonShareParticipants, pageSize]);

  const totalPages = pagination.length;

  const nonShareParticipantsLength = nonShareParticipants.length;

  useEffect(() => {
    setPage(0);
    carousel.current?.goTo?.(0, false);
  }, [pageSize]);

  // 当总页数减少导致当前页越界时，命令式跳到最后一页并修正 page
  useEffect(() => {
    if (totalPages === 0) {
      if (page !== 0) setPage(0);
      return;
    }
    if (page > totalPages - 1) {
      // 越界了。Carousel 会自动重置到 0，我们也必须重置到 0 来保持同步。
      const newPage = 0;
      if (carousel.current && typeof carousel.current.goTo === "function") {
        // 命令式跳转。即使 carousel 内部重置了，goTo(0) 也是安全的。
        carousel.current.goTo(newPage, false);
      }
      setPage(newPage);
    }
  }, [totalPages, page]); // 只在总页数变化时校正

  // 订阅/退订：仅对“当前页的非本地成员”拉取视频，其它页停止
  const runIdRef = useRef(0);
  useEffect(() => {
    // 【新增守卫】
// 如果 totalPages 已经更新，但 page 仍是旧的无效值，
// 立刻停止，等待纠错 hook 触发 setPage 并重渲染。
    if (totalPages > 0 && page > totalPages - 1) {
      return;
    }

    const myRun = ++runIdRef.current;

    const deactivate: any[] = [];
    const activate: any[] = [];
    nonShareParticipants.forEach((p, i) => {
      if (p.local) return;
      const inPage = i >= page * pageSize && i < (page + 1) * pageSize;
      if (p.bInActivePage === inPage) return;
      (inPage ? activate : deactivate).push(p);
    });

    // 翻页必须先释放旧页解码资源，再订阅新页，避免瞬间双倍视频解码压垮浏览器 GPU 进程。
    const switchSubscriptions = async () => {
      await Promise.all(
        deactivate.map((p) =>
          service.setParticipantInActivePage(p.peerId, false).catch(() => undefined)
        )
      );
      if (myRun !== runIdRef.current) return;
      await Promise.all(
        activate.map((p) =>
          service.setParticipantInActivePage(p.peerId, true).catch(() => undefined)
        )
      );
    };
    void switchSubscriptions();

    // 下一轮开始时，使上一轮失效
    return () => {
      runIdRef.current++;
    };
  }, [page, pageSize, nonShareIds, totalPages]); // 成员变更（哪怕长度不变）或翻页时，都会重新跑一轮

  const next = () => {
    if (isMonitorMode) {
      setPage((current) => Math.min(current + 1, totalPages - 1));
    } else {
      carousel.current?.next();
    }
  };
  const prev = () => {
    if (isMonitorMode) {
      setPage((current) => Math.max(current - 1, 0));
    } else {
      carousel.current?.prev();
    }
  };

  const renderGrid = (peers: any[], pageIndex: number) => {
    // 末页不足所选容量时按实际数量收缩宫格，避免少量视频挤在固定大网格中。
    const gridSide = Math.max(1, Math.ceil(Math.sqrt(peers.length)));
    return (
      <div key={`page-${pageIndex}`} className={style.container}>
        <div
          className={style.grid}
          style={{
            gridTemplateColumns: `repeat(${gridSide}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${gridSide}, minmax(0, 1fr))`,
          }}
        >
          {peers.map((d: any) => (
            <div key={d.peerId} className={style.cell}>
              <VideoCard participant={d} compact cover />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={style.pagination}>
      {isMonitorMode ? (
        pagination[page] ? renderGrid(pagination[page], page) : null
      ) : (
        <Carousel ref={carousel} dots={false} infinite={false} afterChange={setPage}>
          {pagination.map(renderGrid)}
        </Carousel>
      )}
      {nonShareParticipantsLength > pageSize && (
        <>
          <Button
            className={style.prev}
            icon={<LeftOutlined />}
            shape="circle"
            disabled={page <= 0}
            onClick={prev}
          />
          <Button
            className={style.next}
            icon={<RightOutlined />}
            shape="circle"
            disabled={page >= totalPages - 1}
            onClick={next}
          />
        </>
      )}
    </div>
  );
};

export default GalleryLayout;
